import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FormPageLayout } from '@components/common';
import BorrowingForm from '@components/common/forms/BorrowingForm';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const ResourceBorrowPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useLanguage();
    const notify = useNotification();

    const targetUserId = searchParams.get('target_user_id');

    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchResource = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/resources/${id}`);
                setResource(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch resource:', err);
                setError(t('resources.resourceBorrow.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [id, t]);

    const handleSubmit = async (formData) => {
        try {
            const response = await api.borrowings.create({
                resource_id: id,
                ...formData
            });
            notify.success(t('borrowing.borrowed_successfully'));
            navigate(`/borrowings/${response.data.id}`);
        } catch (err) {
            console.error('Failed to borrow resource:', err);
            notify.error(t('resources.resourceBorrow.borrowError'));
            throw err;
        }
    };

    const handleCancel = () => {
        navigate(`/resources/${id}`);
    };

    if (loading) {
        return (
            <FormPageLayout
                title={t('resources.resourceBorrow.borrowResource')}
                subtitle={t('resources.resourceBorrow.loading')}
                onBack={handleCancel}
                loading={true}
            />
        );
    }

    if (error || !resource) {
        return (
            <FormPageLayout
                title={t('resources.resourceBorrow.borrowResource')}
                subtitle={error || t('resources.resourceBorrow.notFound')}
                onBack={handleCancel}
                error={true}
            />
        );
    }

    return (
        <FormPageLayout
            title={t('resources.resourceBorrow.borrowResource')}
            subtitle={t('resources.resourceBorrow.subtitle', { resourceName: resource.name })}
            onBack={handleCancel}
        >
            <BorrowingForm
                resource={resource}
                mode="borrow"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                targetUserId={targetUserId ? parseInt(targetUserId) : null}
            />
        </FormPageLayout>
    );
};

export default ResourceBorrowPage;
